import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  Save, 
  Plus, 
  Trash2, 
  RotateCcw, 
  ToggleLeft, 
  ToggleRight,
  Type,
  Image as ImageIcon,
  Link as LinkIcon
} from "lucide-react";

const AdminFeaturesSectionV3 = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  
  const [formData, setFormData] = useState({
    isActive: true,
    leftShapeImage: "",
    subtitle: "",
    titleLine1: "",
    titleLine2: "",
    description: "",
    services: [],
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://allinoneai-d1a5a1b54c64.herokuapp.com/api/services-section-v3");
      setFormData(res.data.data);
      setMessage({ type: "", text: "" });
    } catch (error) {
      setMessage({ type: "danger", text: "Failed to load data." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Handlers ---

  // 1. Simple flat state changes
  const setSimpleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // 2. Array Item Changes
  const handleServicesChange = (index, field, value) => {
    const updatedServices = [...formData.services];
    updatedServices[index][field] = value;
    setFormData({ ...formData, services: updatedServices });
  };

  const addService = () => {
    setFormData({
      ...formData,
      services: [
        ...formData.services,
        {
          id: `srv_${Date.now()}`,
          icon: "assets/img/service/icon-1.png",
          title: "New Service Title",
          description: "Service description goes here...",
          link: "service-details.html",
          delay: ".2s"
        },
      ],
    });
  };

  const removeService = (index) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      const updatedServices = formData.services.filter((_, i) => i !== index);
      setFormData({ ...formData, services: updatedServices });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://allinoneai-d1a5a1b54c64.herokuapp.com/api/services-section-v3", formData);
      setMessage({ type: "success", text: "Services updated successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error(error);
      setMessage({ type: "danger", text: "Failed to update." });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm("Reset all settings to default?")) {
      try {
        await axios.post("https://allinoneai-d1a5a1b54c64.herokuapp.com/api/services-section-v3/reset");
        fetchData();
        setMessage({ type: "success", text: "Reset to defaults." });
      } catch (error) {
        setMessage({ type: "danger", text: "Failed to reset." });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">Manage Services</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${formData.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={() => setSimpleChange('isActive', !formData.isActive)}
            >
                {formData.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                {formData.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset}>
            <RotateCcw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage({type: '', text: ''})}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* --- 1. Header & Shape Image --- */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Type size={20} /> Section Header
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Subtitle</label>
                        <input type="text" className="form-control" value={formData.subtitle} onChange={e => setSimpleChange('subtitle', e.target.value)} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Title Line 1</label>
                        <input type="text" className="form-control" value={formData.titleLine1} onChange={e => setSimpleChange('titleLine1', e.target.value)} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Title Line 2</label>
                        <input type="text" className="form-control" value={formData.titleLine2} onChange={e => setSimpleChange('titleLine2', e.target.value)} />
                    </div>
                    <div className="mb-0">
                        <label className="form-label small fw-bold">Description</label>
                        <textarea className="form-control" rows="3" value={formData.description} onChange={e => setSimpleChange('description', e.target.value)} />
                    </div>
                </div>
            </div>
          </div>

          

          {/* --- 2. Services List Manager --- */}
          <div className="col-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <LinkIcon size={20} /> Services List
                    </div>
                    <button className="btn btn-sm btn-primary" onClick={addService}><Plus size={16} /> Add Service</button>
                </div>
                <div className="card-body">
                    {formData.services.map((service, index) => (
                        <div key={service.id} className="row g-2 align-items-start mb-3 pb-3 border-bottom">
                            <div className="col-12 mb-2">
                                <div className="d-flex justify-content-between">
                                    <span className="badge bg-secondary">Item #{index + 1}</span>
                                    <button className="btn btn-sm btn-link text-danger" onClick={() => removeService(index)}><Trash2 size={14} /></button>
                                </div>
                            </div>
                            
                            <div className="row g-2">
                                <div className="col-md-6 mb-2">
                                    <label className="small text-muted">Title</label>
                                    <input type="text" className="form-control form-control-sm" value={service.title} onChange={e => handleServicesChange(index, 'title', e.target.value)} />
                                </div>
                                <div className="col-md-6 mb-2">
                                    <label className="small text-muted">Link URL</label>
                                    <input type="text" className="form-control form-control-sm" value={service.link} onChange={e => handleServicesChange(index, 'link', e.target.value)} />
                                </div>
                                <div className="col-md-12 mb-2">
                                    <label className="small text-muted">Icon URL</label>
                                    <input type="text" className="form-control form-control-sm" value={service.icon} onChange={e => handleServicesChange(index, 'icon', e.target.value)} />
                                </div>
                            </div>
                            
                            <div className="col-12 mb-2">
                                <label className="small text-muted fw-bold">Description</label>
                                <textarea className="form-control form-control-sm" rows="3" value={service.description} onChange={e => handleServicesChange(index, 'description', e.target.value)} />
                            </div>

                            <div className="col-md-6 mb-0">
                                <label className="small text-muted">Delay (Animation)</label>
                                <input type="text" className="form-control form-control-sm" placeholder="e.g. .2s" value={service.delay} onChange={e => handleServicesChange(index, 'delay', e.target.value)} />
                            </div>
                        </div>
                    ))}
                    {formData.services.length === 0 && <p className="text-center text-muted py-4">No services found. Add one to get started.</p>}
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AdminFeaturesSectionV3;